import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { toast } from 'react-hot-toast';
import { clearTokens, getAccessToken, getRefreshToken, setTokens } from '@/lib/utils/auth';

declare const process: any;

const normalizeBaseUrl = (value: string) => {
  const v = (value || '').trim();
  if (!v) return v;
  if (v.startsWith('http://') || v.startsWith('https://') || v.startsWith('/')) return v;
  return `http://${v}`;
};

const getDefaultApiBaseUrl = (): string => {
  const raw = (process.env.NEXT_PUBLIC_API_URL || process.env.API_URL || '').trim();
  const normalized = normalizeBaseUrl(raw).replace(/\/$/, '');
  if (!normalized) {
    throw new Error('NEXT_PUBLIC_API_URL is required');
  }
  return normalized;
};

const baseURL = getDefaultApiBaseUrl().replace(/\/$/, '');

const normalizeApiDetailToString = (detail: any): string | null => {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const message = detail
      .map((d) => {
        if (typeof d === 'string') return d;
        if (d && typeof d === 'object') {
          const loc = Array.isArray((d as any).loc) ? (d as any).loc.join('.') : '';
          const msg = typeof (d as any).msg === 'string' ? (d as any).msg : '';
          const input = (d as any).input;
          const inputStr =
            input === undefined || input === null ? '' : typeof input === 'string' ? input : JSON.stringify(input);
          const parts = [loc, msg].filter(Boolean).join(': ');
          return inputStr ? `${parts} (input: ${inputStr})` : parts;
        }
        return String(d);
      })
      .filter(Boolean)
      .join('\n');
    return message || null;
  }
  if (detail && typeof detail === 'object') return JSON.stringify(detail);
  return null;
};

// Tạo axios instance với cấu hình mặc định
const axiosInstance: AxiosInstance = axios.create({
  baseURL,
  timeout: 3600000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag và queue để xử lý refresh token concurrent
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (error: any) => void }> = [];
let lastForbiddenToastAt = 0;

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });

  failedQueue = [];
};

const handleRefreshFailure = () => {
  if (typeof window === 'undefined') {
    return;
  }
  
  // Nếu đang trong quá trình logout thì không hiển thị thông báo lỗi
  if (sessionStorage.getItem('logging_out')) {
    clearTokens();
    delete axiosInstance.defaults.headers.common['Authorization'];
    delete (axiosInstance.defaults.headers.common as any)['authorization'];
    
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    return;
  }

  toast.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
  clearTokens();
  delete axiosInstance.defaults.headers.common['Authorization'];
  delete (axiosInstance.defaults.headers.common as any)['authorization'];

  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    handleRefreshFailure();
    throw new Error('Missing refresh token');
  }
  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }
  isRefreshing = true;
  try {
    const response = await axios.post(`${baseURL}/auth/refresh-token`, {
      refresh_token: refreshToken,
    });
    const { access_token, refresh_token: new_refresh_token } = response.data.data;
    setTokens(access_token, new_refresh_token);
    axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    processQueue(null, access_token);
    return access_token;
  } catch (err) {
    processQueue(err, null);
    handleRefreshFailure();
    throw err;
  } finally {
    isRefreshing = false;
  }
};

// Request interceptor - Thêm token vào header nếu có
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();

    if (config.headers) {
      const headers: any = config.headers;
      if (token) {
        if (typeof headers.set === 'function') {
          headers.set('Authorization', `Bearer ${token}`);
        } else {
          headers.Authorization = `Bearer ${token}`;
        }
      } else {
        if (typeof headers.delete === 'function') {
          headers.delete('Authorization');
        } else {
          delete headers.Authorization;
          delete headers.authorization;
        }
      }
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Xử lý lỗi chung
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const requestUrl = originalRequest?.url || '';
    const isRefreshRequest = requestUrl.includes('/auth/refresh-token');
    const isLogoutRequest = requestUrl.includes('/auth/logout');
    const isLoginRequest = requestUrl.includes('/auth/login');

    if (error.response?.status === 422) {
      const data: any = error.response?.data as any;
      if (data && typeof data === 'object') {
        const normalized = normalizeApiDetailToString(data.detail);
        if (normalized) data.detail = normalized;
      }
    }

    // Xử lý lỗi 401 (Unauthorized)
    if (error.response?.status === 401) {
      if (isLoginRequest) {
        const detail = (error.response?.data as any)?.detail;
        const normalizedDetail = normalizeApiDetailToString(detail);
        toast.error(normalizedDetail || 'Sai tên đăng nhập hoặc mật khẩu');
        return Promise.reject(error);
      }

      if (!originalRequest._retry) {
        if (isLogoutRequest) {
          return Promise.reject(error);
        }
        if (isRefreshRequest) {
          handleRefreshFailure();
          return Promise.reject(error);
        }
        // Nếu đang refresh thì add vào queue
        if (isRefreshing) {
          return new Promise<string>((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              return axiosInstance(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        try {
          const accessToken = await refreshAccessToken();
          if (originalRequest.headers) {
            const headers: any = originalRequest.headers;
            if (typeof headers.set === 'function') {
              headers.set('Authorization', `Bearer ${accessToken}`);
            } else {
              headers.Authorization = `Bearer ${accessToken}`;
            }
          }
          return axiosInstance(originalRequest);
        } catch (err) {
          return Promise.reject(err);
        }
      }
    }

    // Xử lý lỗi 403 (Forbidden)
    if (error.response?.status === 403) {
      console.error('Access forbidden');

      const detail = (error.response?.data as any)?.detail;
      const isAuthError =
        detail === 'User not found' ||
        detail === 'Could not validate credentials' ||
        detail === 'Không tìm thấy người dùng';

      const isMeRequest = requestUrl.endsWith('/me') || requestUrl.endsWith('/me/');

      if (isAuthError || isMeRequest) {
        handleRefreshFailure();
        return Promise.reject(error);
      }

      if (!isLogoutRequest) {
        const now = Date.now();
        if (now - lastForbiddenToastAt > 1500) {
          lastForbiddenToastAt = now;
          const normalizedDetail = normalizeApiDetailToString(detail);
          toast.error(normalizedDetail || 'Bạn không có quyền thực hiện thao tác này');
        }
      }
    }

    // Xử lý lỗi 500 (Server Error)
    if (error.response?.status === 500) {
      console.error('Server error');
      if (!isLogoutRequest) toast.error('Lỗi hệ thống, vui lòng thử lại sau');
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
